/**
 * V2 Import your Google reviews — /import/reviews (wave 2, step 5).
 *
 * "You reviewed these places — bring the ones worth keeping into your
 * journal." Intro (how to get the file) → file pick → paced matching →
 * review screen (tick the keepers; stars shown for recognition only) →
 * a stamping-style walk per keeper: place resolved, YOUR WORDS and the
 * VISIT DATE prefilled, you add the photo (required — standing decision
 * 2) and pick the category (the honest wrinkle: reviews carry none; the
 * type-ahead uses the log form's taggability rule, prefilled from the
 * V1 yoink_last_category key).
 *
 * The location row is created only when a review is actually stamped
 * (the 2026-07-27 deferred-creation rule); star ratings are never
 * written (design decision 2026-08-04).
 */
import * as gateway from '../../core/gateway.js';
import { invalidate } from '../../core/query.js';
import { escapeHtml } from '../../../assets/js/utils.js';
import { fetchAllCategories } from '../../core/categories.js';
import { categoryIcon } from '../../ui/icons.js';
import { createLocationFromPlace, fetchPlacePhoto } from '../log/places.js';
import { resolvePhotos } from '../log/log-data.js';
import { validatePhotoPick, toDateInput } from '../log/log-validate.js';
import { markComplete } from '../community/tutorial.js';
import { syncAwards } from '../profile/awards.js';
import { crossOffWantToGo } from '../lists/lists.js';
import { READ_ONLY_MESSAGE } from '../../core/settings.js';
import { reviewRows, resolveReviewRows, MAX_REVIEW_ROWS } from './reviews.js';

const ROW_STATUS = {
  matched: (row) => escapeHtml(row.place.secondaryText || ''),
  uncertain: (row) => `Best guess — your review says “${escapeHtml(row.name)}”`,
  closed: () => '<span class="list-entry-closed">Permanently closed</span> — tick it only if you want the memory',
  unmatched: () => 'Couldn’t match this one',
  rejected: () => 'This link points at an area, not a place',
  already: () => 'Already in your journal',
};

// Attention-first, same idiom as the Maps importer: misses and the ones
// you already stamped sit above the ready rows.
const STATUS_RANK = { unmatched: 0, rejected: 0, already: 1, closed: 2, uncertain: 3, matched: 4 };

function stars(n) {
  return n >= 1 && n <= 5 ? '★'.repeat(Math.round(n)) : '';
}

export function mount(el, ctx) {
  const { store, router } = ctx;
  const basePath = window.YOINK_CONFIG?.basePath || '/adventure';
  let destroyed = false;
  let catalog = [];
  let resolved = []; // rows + { status, place? }
  let checked = new Set();
  let stopAsked = false;
  let capNote = '';
  // The walk
  let queue = []; // resolved rows still to stamp
  let current = null;
  let photoItems = [];
  let photoUrls = [];
  let visitDate = '';
  let note = '';
  let category = null;
  let stamping = false;
  let cardError = '';
  let stamped = 0;
  let skipped = []; // the resolved rows put aside — a real, resumable queue
  // The place's own Google photo for THIS card: the stand-in when a
  // review is too old to have a photo of your own (2026-08-04). null =
  // not looked up yet, false = Google has none.
  let placePhoto = null;
  let usePlacePhoto = false;

  // A reviews import is a multi-sitting job too, and "Skip for now" used
  // to promise a waiting list that did not exist — the names were printed
  // once and lost (2026-08-04 on-device). The whole session persists per
  // device now, skips included.
  const SESSION_KEY = 'yoink_reviews_session';

  function saveSession() {
    if (!resolved.length && !skipped.length) return;
    try {
      localStorage.setItem(SESSION_KEY, JSON.stringify({
        resolved, checked: [...checked], queue, skipped, stamped, capNote,
      }));
    } catch { /* quota/private mode — resuming just won't survive a leave */ }
  }

  function clearSession() {
    try {
      localStorage.removeItem(SESSION_KEY);
    } catch { /* private mode */ }
  }

  function restoreSession() {
    try {
      const s = JSON.parse(localStorage.getItem(SESSION_KEY) || 'null');
      if (!s || !Array.isArray(s.resolved) || !s.resolved.length) return false;
      const savedQueue = Array.isArray(s.queue) ? s.queue : [];
      const savedSkipped = Array.isArray(s.skipped) ? s.skipped : [];
      // Nothing left to work → drop it rather than half-loading state a
      // fresh import would then inherit (a stale `stamped` threw off the
      // next walk's "3 of 12" counter)
      if (!savedQueue.length && !savedSkipped.length) {
        clearSession();
        return false;
      }
      resolved = s.resolved;
      checked = new Set(Array.isArray(s.checked) ? s.checked : []);
      queue = savedQueue;
      skipped = savedSkipped;
      stamped = Number(s.stamped) || 0;
      capNote = s.capNote || '';
      return true;
    } catch {
      return false;
    }
  }

  function renderResume() {
    el.innerHTML = `
      ${back}
      <section class="v2-import" aria-label="Pick up where you left off">
        <h2 class="v2-page-title">Pick up where you left off?</h2>
        <p>${stamped} stamped${queue.length ? ` · ${queue.length} still to go` : ''}${skipped.length
          ? ` · ${skipped.length} set aside` : ''}. Nothing is looked up twice.</p>
        ${queue.length ? `<button type="button" class="v2-btn-leather" data-rev-continue>
          Continue — ${queue.length} to go</button>` : ''}
        ${skipped.length ? `<button type="button" class="v2-btn-${queue.length ? 'more' : 'leather'}" data-rev-skipped>
          Work the ${skipped.length} I set aside</button>` : ''}
        <button type="button" class="v2-btn-more" data-rev-again>Start over with a new file</button>
      </section>`;
  }

  const back = `<nav class="v2-back" aria-label="Back">
    <a href="${basePath}/import" data-back-import>← Bring your places</a></nav>`;

  function taggable(cat) {
    return cat.can_tag !== false && !cat.is_set;
  }

  function lastUsedCategory() {
    try {
      const id = localStorage.getItem('yoink_last_category');
      const cat = id && catalog.find((c) => c.id === id);
      return cat && taggable(cat) ? cat : null;
    } catch {
      return null;
    }
  }

  function clearPhotoUrls() {
    photoUrls.forEach((u) => URL.revokeObjectURL(u));
    photoUrls = [];
  }

  function renderIntro(error = '') {
    el.innerHTML = `
      ${back}
      <section class="v2-import" aria-label="Import your Google reviews">
        <h2 class="v2-page-title">Import your Google reviews</h2>
        <p>Every review you’ve written is a visit with the words already written.
          Bring the ones worth keeping into your journal — your text and date come
          along; you add a photo and a category.</p>
        <ol class="import-steps">
          <li>Open <a class="import-takeout-link" href="https://takeout.google.com" target="_blank"
            rel="noopener"><strong>takeout.google.com</strong></a> and sign in.</li>
          <li>Tap “Deselect all”, then check only <strong>Maps (your places)</strong>.</li>
          <li>Tap “Next step”, then “Create export”, and download the file when it’s ready.</li>
          <li>Unzip it — inside there’s a <strong>Reviews</strong> file (a .json).</li>
        </ol>
        <label class="v2-btn-leather import-file-btn">Choose the Reviews file
          <input type="file" accept=".json,application/json" data-rev-file hidden>
        </label>
        <p class="log-hint import-error" data-rev-error aria-live="polite">${escapeHtml(error)}</p>
      </section>`;
  }

  function renderMatching(total) {
    el.innerHTML = `
      ${back}
      <section class="v2-import" aria-label="Matching your reviews">
        <h2 class="v2-page-title">Matching your reviews…</h2>
        <p data-match-line aria-live="polite">Matching 1 of ${total}…</p>
        <progress class="import-progress" data-match-bar max="${total}" value="0"></progress>
        <p class="log-hint">Google limits how fast places can be looked up — keep this
          page open.${capNote ? ` ${escapeHtml(capNote)}` : ''}</p>
        <button type="button" class="v2-btn-more" data-rev-stop>Stop and review what’s matched</button>
      </section>`;
  }

  function reviewRow(row, i) {
    const name = row.place ? row.place.mainText : row.name || 'Untitled place';
    const sub = (ROW_STATUS[row.status] || (() => ''))(row);
    const when = row.date ? toDateInput(row.date) : '';
    const excerpt = row.text ? (row.text.length > 140 ? `${row.text.slice(0, 140)}…` : row.text) : '';
    return `
      <label class="import-row ${row.place ? '' : 'import-row-dead'}">
        <input type="checkbox" data-rev-check="${i}" ${checked.has(i) ? 'checked' : ''}
          ${row.place ? '' : 'disabled'}
          ${row.status === 'already' ? 'title="You already have this one — tick it only to log a second visit"' : ''}>
        <span class="import-row-body">
          <strong>${escapeHtml(name)}</strong>
          <span class="import-row-sub">${[stars(row.rating), when].filter(Boolean).join(' · ')}</span>
          ${sub ? `<span class="import-row-sub">${sub}</span>` : ''}
          ${excerpt ? `<span class="import-row-sub">“${escapeHtml(excerpt)}”</span>` : ''}
        </span>
      </label>`;
  }

  function renderReview() {
    // Distinct buckets, so no two numbers here can disagree
    const ready = resolved.filter((r) => r.place && r.status !== 'already').length;
    const alreadyCount = resolved.filter((r) => r.status === 'already').length;
    const missCount = resolved.filter((r) => !r.place).length;
    const countsLine = [
      `${ready} to keep`,
      alreadyCount ? `${alreadyCount} already in your journal` : '',
      missCount ? `${missCount} couldn’t match` : '',
    ].filter(Boolean).join(' · ');
    // Sort the VIEW; checkbox indexes still point into resolved, so
    // ticks survive the reorder
    const order = resolved.map((_, i) => i)
      .sort((a, b) => (STATUS_RANK[resolved[a].status] ?? 4) - (STATUS_RANK[resolved[b].status] ?? 4) || a - b);
    el.innerHTML = `
      ${back}
      <section class="v2-import" aria-label="Pick the reviews worth keeping">
        <h2 class="v2-page-title">Pick the ones worth keeping</h2>
        <p>${countsLine}. ${capNote ? escapeHtml(capNote) : ''}
          Each one you keep becomes a yoink in your journal — you’ll add a photo and a
          category next. (Star ratings stay behind — your words are the keepsake.)</p>
        <div class="import-rows">${order.map((i) => reviewRow(resolved[i], i)).join('')}</div>
        <button type="button" class="v2-btn-leather" data-rev-start
          ${checked.size ? '' : 'disabled'}>Keep ${checked.size} review${checked.size === 1 ? '' : 's'}</button>
      </section>`;
  }

  function categoryPickerHtml() {
    return category
      ? `<div class="log-category-chosen">
           <span class="log-category-pill">${categoryIcon(category, 20)}<span>${escapeHtml(category.name)}</span></span>
           <button type="button" class="log-change-place" data-rev-cat-change>Change</button>
         </div>`
      : `<label class="v2-field">Category
           <input type="search" data-rev-cat-search placeholder="Start typing — tacos, parks…" autocomplete="off">
         </label>
         <div class="log-place-results" data-rev-cat-results aria-live="polite"></div>`;
  }

  function photoListHtml() {
    if (photoItems.length) {
      return `<div class="stamp-photos">${photoUrls
        .map((u) => `<span class="stamp-photo"><img src="${u}" alt=""></span>`)
        .join('')}</div>`;
    }
    if (usePlacePhoto && placePhoto) {
      return `<div class="stamp-photos">
        <span class="stamp-photo"><img src="${escapeHtml(placePhoto)}" alt=""></span>
        <span class="log-hint">The place’s photo from Google — swap in your own anytime by editing the yoink.</span>
      </div>`;
    }
    return '';
  }

  /**
   * The photo row. Your own photo is the ideal, but hunting one down for
   * a review from years ago is a chore that stalls the whole import
   * (2026-08-04), so the place's own Google photo is offered as a
   * stand-in — labelled as such, here and on the yoink page.
   */
  function photoPickerHtml() {
    const mine = photoItems.length > 0;
    return `
      <div class="stamp-photo-choice">
        <label class="v2-btn-${mine ? 'more' : 'leather'} import-file-btn">${mine ? 'Change photo' : 'Pick a photo from your trip'}
          <input type="file" accept="image/*" multiple data-rev-photo hidden>
        </label>
        ${!mine && placePhoto ? `
        <button type="button" class="v2-btn-${usePlacePhoto ? 'more' : 'leather'}" data-rev-use-place>
          ${usePlacePhoto ? '✓ Using the place’s photo' : 'No photo? Use the place’s'}</button>` : ''}
        ${!mine && placePhoto === false ? '<span class="log-hint">Google has no photo for this one — yours or skip.</span>' : ''}
      </div>`;
  }

  function renderCard() {
    const total = stamped + skipped.length + queue.length + 1;
    const at = stamped + skipped.length + 1;
    el.innerHTML = `
      ${back}
      <section class="v2-import" aria-label="Stamp ${escapeHtml(current.place.mainText)}">
        <p class="stamp-progress">${at} of ${total}</p>
        <h2 class="v2-page-title">${escapeHtml(current.place.mainText)}</h2>
        ${current.place.secondaryText ? `<p class="stamp-place-sub">${escapeHtml(current.place.secondaryText)}</p>` : ''}
        <div class="v2-field-block">
          <span class="v2-field-name">Category</span>
          ${categoryPickerHtml()}
        </div>
        ${photoPickerHtml()}
        ${photoListHtml()}
        <label class="v2-field">When were you there?
          <input type="date" name="revDate" value="${visitDate}" max="${toDateInput(new Date())}">
        </label>
        <label class="v2-field">Your words
          <textarea name="revNote" rows="4" maxlength="2000">${escapeHtml(note)}</textarea>
        </label>
        <div class="v2-form-error" data-rev-card-error role="alert">${escapeHtml(cardError)}</div>
        <div class="log-status" data-rev-status aria-live="polite"></div>
        <div class="stamp-actions">
          <button type="button" class="v2-btn-leather" data-rev-save
            ${stamping || !(photoItems.length || (usePlacePhoto && placePhoto)) || !category ? 'disabled' : ''}>
            ${stamping ? 'Stamping…' : 'Stamp it'}</button>
          <button type="button" class="v2-btn-more" data-rev-skip ${stamping ? 'disabled' : ''}>Skip for now</button>
        </div>
      </section>`;
  }

  function renderDone() {
    el.innerHTML = `
      ${back}
      <section class="v2-import" aria-label="Import finished">
        <h2 class="v2-page-title">${stamped ? 'Stamped!' : 'All done'}</h2>
        ${stamped ? `<p>${stamped} review${stamped === 1 ? ' is' : 's are'} in your journal now — words, dates, and all.</p>` : ''}
        ${skipped.length ? `
        <div class="hub-card">
          <h3>Set aside for later (${skipped.length})</h3>
          <p>${skipped.map((r) => escapeHtml(r.place?.mainText || r.name || 'a place')).join(', ')}</p>
          <p class="log-hint">They’re saved on this device — this page offers them back
            whenever you return, even after you close the tab.</p>
          <button type="button" class="v2-btn-leather" data-rev-skipped>Work through them now</button>
        </div>` : ''}
        <button type="button" class="v2-btn-${skipped.length ? 'more' : 'leather'}" data-rev-journal>See your journal</button>
        <button type="button" class="v2-btn-more" data-rev-again>Import another file</button>
      </section>`;
  }

  async function onFile(file) {
    let text;
    try {
      text = await file.text();
    } catch {
      renderIntro('Couldn’t read that file — try picking it again.');
      return;
    }
    let rows = reviewRows(text);
    if (!rows.length) {
      renderIntro('That doesn’t look like a Google reviews export — pick the Reviews .json inside Maps (your places).');
      return;
    }
    capNote = rows.length > MAX_REVIEW_ROWS ? `(Reading the first ${MAX_REVIEW_ROWS} of ${rows.length}.)` : '';
    rows = rows.slice(0, MAX_REVIEW_ROWS);
    stopAsked = false;
    // A new file starts a new walk — never inherit the last one's tallies
    stamped = 0;
    skipped = [];
    queue = [];
    renderMatching(rows.length);
    resolved = await resolveReviewRows(rows, {
      shouldStop: () => destroyed || stopAsked,
      onRow: (row, i, total) => {
        const line = el.querySelector('[data-match-line]');
        const bar = el.querySelector('[data-match-bar]');
        if (line) {
          line.textContent = i + 1 < total
            ? `Matching ${i + 2} of ${total}… (${row.name || row.place?.mainText || ''} ${row.place ? '✓' : '·'})`
            : `Matched ${total} of ${total}.`;
        }
        if (bar) bar.value = i + 1;
      },
    });
    if (destroyed) return;
    await markAlreadyStamped(resolved);
    if (destroyed) return;
    checked = new Set(resolved.map((r, i) => (r.status === 'matched' ? i : null)).filter((i) => i !== null));
    saveSession();
    renderReview();
  }

  /**
   * Reviews whose place is ALREADY in your journal (2026-08-04 round 3:
   * re-running the file re-offered everything, including reviews stamped
   * on the previous pass — which is also the only way back for skips
   * lost before sessions existed). Pure DB lookups on google_place_id;
   * a failure quietly leaves rows as plain matches.
   */
  async function markAlreadyStamped(rows) {
    const me = store.get().user;
    const withPlace = rows.filter((r) => r.place?.placeId);
    if (!me || !withPlace.length) return;
    try {
      const placeIds = [...new Set(withPlace.map((r) => r.place.placeId))];
      const { data: locs } = await gateway.rawQuery(gateway.TABLES.LOCATIONS, {
        select: 'id,google_place_id',
        filters: { google_place_id: `in.(${placeIds.join(',')})` },
        limit: placeIds.length,
      });
      const byPlace = new Map((locs || []).map((l) => [l.google_place_id, l.id]));
      const locIds = [...byPlace.values()];
      if (!locIds.length) return;
      const { data: mine } = await gateway.rawQuery(gateway.TABLES.YOINKS, {
        select: 'id,location_id',
        filters: { user_id: `eq.${me.id}`, location_id: `in.(${locIds.join(',')})` },
        limit: locIds.length,
      });
      const have = new Set((mine || []).map((y) => y.location_id));
      for (const row of withPlace) {
        const locId = byPlace.get(row.place.placeId);
        if (locId && have.has(locId)) row.status = 'already';
      }
    } catch { /* a nicety, not a gate */ }
  }

  function nextCard() {
    clearPhotoUrls();
    photoItems = [];
    cardError = '';
    stamping = false;
    placePhoto = null;
    usePlacePhoto = false;
    if (!queue.length) {
      current = null;
      if (stamped) {
        invalidate();
        markComplete('log_yoink');
        const user = store.get().user;
        if (user) syncAwards(store, user.id, { announce: true }).catch(() => {});
      }
      saveSession();
      renderDone();
      return;
    }
    current = queue.shift();
    visitDate = current.date ? toDateInput(current.date) : '';
    note = current.text || '';
    category = category && taggable(category) ? category : lastUsedCategory();
    saveSession();
    renderCard();
    // The stand-in photo, looked up while they read the card
    const forRow = current;
    fetchPlacePhoto(current.place?.placeId).then((url) => {
      if (destroyed || current !== forRow) return;
      placePhoto = url || false;
      renderCard();
    });
  }

  async function stampCurrent() {
    const user = store.get().user;
    if (!user || !current || stamping) return;
    if (!category) {
      cardError = 'Pick a category first.';
      renderCard();
      return;
    }
    if (!photoItems.length && !(usePlacePhoto && placePhoto)) {
      cardError = 'Add a photo first — yours, or the place’s.';
      renderCard();
      return;
    }
    if (visitDate && visitDate > toDateInput(new Date())) {
      cardError = 'The date can’t be in the future.';
      renderCard();
      return;
    }
    stamping = true;
    cardError = '';
    renderCard();
    const setStatus = (text) => {
      const line = el.querySelector('[data-rev-status]');
      if (line) line.textContent = text;
    };
    try {
      const result = await createLocationFromPlace(current.place);
      if (result.rejected) throw new Error('This link points at an area, not a place.');
      if (destroyed) return;
      // Your own photo uploads; the place's stand-in is stored as the
      // proxy URL itself (core/photo.js resolves both shapes)
      const images = photoItems.length
        ? await resolvePhotos(user.id, photoItems, setStatus)
        : [placePhoto];
      if (destroyed) return;
      setStatus('Saving…');
      const body = {
        user_id: user.id,
        location_id: result.location.id,
        category_id: category.id,
        title: result.location.display_name,
        description: note.trim() || null,
        social_url: null,
        images,
      };
      // Step-1 backdate rule: the picked date keeps the current
      // time-of-day so a run of stamps stays in tap order.
      if (visitDate) {
        const [y, m, d] = visitDate.split('-').map(Number);
        const when = new Date();
        when.setFullYear(y, m - 1, d);
        body.created_at = when.toISOString();
      }
      const { error } = await gateway.rawInsert(gateway.TABLES.YOINKS, { body });
      if (error) throw error;
      try {
        localStorage.setItem('yoink_last_category', category.id); // V1 key
      } catch { /* private mode */ }
      crossOffWantToGo(store, result.location.id).catch(() => {});
      stamped += 1;
      nextCard();
    } catch (err) {
      stamping = false;
      cardError = err?.message ? `Couldn’t stamp it — ${err.message}` : 'Couldn’t stamp it — try again.';
      renderCard();
    }
  }

  function renderCategoryResults(term) {
    const resultsEl = el.querySelector('[data-rev-cat-results]');
    if (!resultsEl) return;
    const needle = term.trim().toLowerCase();
    if (!needle) {
      resultsEl.innerHTML = '';
      return;
    }
    // Same as the log form: search means intent, so the browse-page parks
    // toggle doesn't hide Parks & Protected Areas from tagging search here.
    const matches = catalog
      .filter((c) => taggable(c) && c.name.toLowerCase().includes(needle))
      .slice(0, 12);
    resultsEl.innerHTML = matches.length
      ? matches.map((c) => `
          <button type="button" class="log-place-option" data-rev-cat-pick="${c.id}">
            <strong>${escapeHtml(c.name)}</strong>
          </button>`).join('')
      : '<div class="log-hint">No category by that name.</div>';
  }

  function onChange(event) {
    const file = event.target.closest('[data-rev-file]');
    if (file && file.files?.[0]) {
      onFile(file.files[0]);
      return;
    }
    const check = event.target.closest('[data-rev-check]');
    if (check) {
      const i = Number(check.dataset.revCheck);
      if (check.checked) checked.add(i);
      else checked.delete(i);
      const start = el.querySelector('[data-rev-start]');
      if (start) {
        start.disabled = !checked.size;
        start.textContent = `Keep ${checked.size} review${checked.size === 1 ? '' : 's'}`;
      }
      return;
    }
    const photo = event.target.closest('[data-rev-photo]');
    if (photo && photo.files?.length) {
      const { accepted, error } = validatePhotoPick([], [...photo.files]);
      cardError = error;
      if (accepted.length) {
        clearPhotoUrls();
        photoItems = accepted;
        photoUrls = photoItems.map((item) => URL.createObjectURL(item.file));
      }
      renderCard();
      return;
    }
    if (event.target.name === 'revDate') visitDate = event.target.value;
    if (event.target.name === 'revNote') note = event.target.value;
  }

  function onInput(event) {
    if (event.target.closest('[data-rev-cat-search]')) renderCategoryResults(event.target.value);
    if (event.target.name === 'revNote') note = event.target.value;
  }

  function onClick(event) {
    if (event.target.closest('[data-rev-stop]')) {
      stopAsked = true;
      return;
    }
    if (event.target.closest('[data-rev-start]')) {
      queue = resolved.filter((r, i) => checked.has(i) && r.place);
      if (queue.length) nextCard();
      return;
    }
    const pick = event.target.closest('[data-rev-cat-pick]');
    if (pick) {
      category = catalog.find((c) => c.id === pick.dataset.revCatPick) || null;
      renderCard();
      return;
    }
    if (event.target.closest('[data-rev-cat-change]')) {
      category = null;
      renderCard();
      return;
    }
    if (event.target.closest('[data-rev-save]')) {
      stampCurrent();
      return;
    }
    if (event.target.closest('[data-rev-use-place]')) {
      usePlacePhoto = !usePlacePhoto;
      cardError = '';
      renderCard();
      return;
    }
    if (event.target.closest('[data-rev-skip]')) {
      if (current) skipped.push(current); // a real queue, not just a name
      nextCard();
      return;
    }
    if (event.target.closest('[data-rev-continue]')) {
      nextCard();
      return;
    }
    if (event.target.closest('[data-rev-skipped]')) {
      queue = queue.concat(skipped);
      skipped = [];
      nextCard();
      return;
    }
    if (event.target.closest('[data-rev-journal]')) {
      const username = store.get().profile?.username;
      if (username) router.navigate('user', { username });
      return;
    }
    if (event.target.closest('[data-rev-again]')) {
      clearSession();
      resolved = [];
      checked = new Set();
      capNote = '';
      stamped = 0;
      skipped = [];
      queue = [];
      renderIntro();
      return;
    }
    const backLink = event.target.closest('[data-back-import]');
    if (backLink) {
      event.preventDefault();
      router.navigate('import');
    }
  }

  async function start() {
    const state = store.get();
    if (!state.authReady) {
      const unsub = store.subscribe((s) => {
        if (!s.authReady || destroyed) return;
        unsub();
        start();
      });
      el.innerHTML = '<div class="v2-loading">Unrolling the map…</div>';
      return;
    }
    if (!state.user) {
      el.innerHTML = `${back}<div class="v2-empty">Sign in (top right) to import your reviews.</div>`;
      return;
    }
    if (state.isReadOnly) {
      el.innerHTML = `${back}<div class="v2-empty">${READ_ONLY_MESSAGE}</div>`;
      return;
    }
    try {
      catalog = await fetchAllCategories();
    } catch {
      catalog = [];
    }
    if (destroyed) return;
    // Unfinished work outranks a fresh start — including the skips,
    // which used to evaporate the moment you left
    if (restoreSession()) renderResume();
    else renderIntro();
  }

  el.addEventListener('change', onChange);
  el.addEventListener('input', onInput);
  el.addEventListener('click', onClick);
  const onPageHide = () => saveSession();
  window.addEventListener('pagehide', onPageHide);
  start();

  return () => {
    destroyed = true;
    saveSession();
    window.removeEventListener('pagehide', onPageHide);
    clearPhotoUrls();
    el.removeEventListener('change', onChange);
    el.removeEventListener('input', onInput);
    el.removeEventListener('click', onClick);
  };
}
